import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

// Feature-scoped API module for Drive-Thru ANPR. Uses the shared axios instance
// (Bearer auth + 401 handling) like the rest of the admin panel. Read calls return
// data (or [] / null) and toast on error; mutations return the created/updated record
// (or null) and let the caller toast success.

// ------------------------------------------------------------------- Meta
// Supported camera brands / capture point types / event statuses come from the
// backend (CAMERA_BRANDS etc.) so the UI never hardcodes — add a brand server-side
// and it shows up here automatically.
export const getDriveThruMeta = async () => {
  try {
    const res = await axiosInstance.get("/api/admin/drive-thru/meta");
    if (res?.data && !res.data.isError) return res.data.data;
    return { cameraBrands: [], capturePointTypes: [], eventStatuses: [] };
  } catch (error) {
    errorToast(error?.message ?? "Error loading drive-thru options");
    return { cameraBrands: [], capturePointTypes: [], eventStatuses: [] };
  }
};

// ----------------------------------------------------------------- Lanes
export const getLanes = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/admin/drive-thru/lanes", { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading lanes");
    return [];
  } catch (error) {
    errorToast(error?.message ?? "Error loading lanes");
    return [];
  }
};

export const createLane = async (payload) => {
  try {
    const res = await axiosInstance.post("/api/admin/drive-thru/lanes", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error creating lane");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error creating lane");
    return null;
  }
};

export const updateLane = async (id, payload) => {
  try {
    const res = await axiosInstance.put(`/api/admin/drive-thru/lanes/${id}`, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating lane");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error updating lane");
    return null;
  }
};

// -------------------------------------------------------- Capture points
export const getCapturePoints = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/admin/drive-thru/capture-points", { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading capture points");
    return [];
  } catch (error) {
    errorToast(error?.message ?? "Error loading capture points");
    return [];
  }
};

export const createCapturePoint = async (payload) => {
  try {
    const res = await axiosInstance.post("/api/admin/drive-thru/capture-points", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error creating capture point");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error creating capture point");
    return null;
  }
};

export const updateCapturePoint = async (id, payload) => {
  try {
    const res = await axiosInstance.put(`/api/admin/drive-thru/capture-points/${id}`, payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating capture point");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error updating capture point");
    return null;
  }
};

// ------------------------------------------------------------ POS devices
export const getDriveThruDevices = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/admin/drive-thru/pos-devices", { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading devices");
    return [];
  } catch (error) {
    errorToast(error?.message ?? "Error loading devices");
    return [];
  }
};

// assignedCapturePointId = null unassigns the device.
export const assignDeviceCapturePoint = async (deviceId, assignedCapturePointId) => {
  try {
    const res = await axiosInstance.put(`/api/admin/drive-thru/pos-devices/${deviceId}/assign`, {
      assignedCapturePointId,
    });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error assigning device");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error assigning device");
    return null;
  }
};

// ------------------------------------------------------------ ANPR events
export const getAnprEvents = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/admin/drive-thru/anpr-events", { params });
    if (res?.data && !res.data.isError) return res.data.data; // { events, pageNo, pageSize, totalRecords, totalPages }
    errorToast(res?.data?.message ?? "Error loading events");
    return null;
  } catch (error) {
    errorToast(error?.message ?? "Error loading events");
    return null;
  }
};

// The snapshot endpoint requires Bearer auth, so an <img src> can't hit it directly.
// We fetch it as a blob and hand back an object URL (caller must revokeObjectURL).
export const fetchAnprSnapshotObjectUrl = async (eventId) => {
  try {
    const res = await axiosInstance.get(`/api/admin/drive-thru/anpr-events/${eventId}/snapshot`, {
      responseType: "blob",
    });
    return URL.createObjectURL(res.data);
  } catch (error) {
    return null;
  }
};
